class PredictiveSearch extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';

    const isTemplateSearch = this.closest('.template-search');
    if (isTemplateSearch) {
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  onFocus(event) {
    if (this.closest('.template-search')) {
      document.body.classList.add('predictive-search-open');
      if (this.getQuery().length === 0) {
        return;
      }
      const url = this.setupURL().toString();
      this.renderSection(url, event);
    }
  }
  getQuery() {
    return this.input.value.trim();
  }
  clear(event = null) {
    event && event.preventDefault();
    if (this.searchRecommendationEmpty && this.searchContent) {
      this.searchContent.classList.add('hidden');
    }
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    return (
      url.searchParams.set('q', this.getQuery()),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    // Indicate that loading has started
    this.setAttribute('loading', 'true');
    this.resetButton.classList.add('btn--loading');

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.text();
      })
      .then((responseText) => {
        // Process and cache the fetched data
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Remove the loading attribute in both success and error cases
        this.removeAttribute('loading');
        this.resetButton.classList.remove('btn--loading');
        this.setAttribute('results', 'true');
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleClickOutside(event) {
    if (!this.contains(event.target)) {
      setTimeout(() => {
        document.body.classList.remove('predictive-search-open');
      });
    }
  }
}
customElements.define('predictive-search', PredictiveSearch, { extends: 'form' });

// --- MonBlari: force full search for model-like queries (e.g. XA-060, XA060) ---
(function () {
  function looksLikeModel(q) {
    if (!q) return false;
    const s = q.trim();
    if (s.length < 4) return false;

    // only allow letters/numbers/hyphen/space
    if (!/^[a-zA-Z0-9\-\s]+$/.test(s)) return false;

    // must contain at least one letter and one digit (model pattern)
    const hasLetter = /[a-zA-Z]/.test(s);
    const hasDigit = /\d/.test(s);
    if (!hasLetter || !hasDigit) return false;

    return true;
  }

  // event delegation: works for drawer/modal and future re-renders
  document.addEventListener("keydown", function (e) {
    const el = e.target;
    if (!el) return;

    // Shopify predictive search input is usually type="search"
    if (e.key === "Enter" && el.matches('input[type="search"]')) {
      const q = (el.value || "").trim();
      if (looksLikeModel(q)) {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = "/search?q=" + encodeURIComponent(q);
      }
    }
  }, true);
})();

class SearchDrawer extends DrawerComponent {
  constructor() {
    super();
  }
  get requiresBodyAppended() {
    return false;
  }
  get input() {
    return this.querySelector('input[type="search"]');
  }
  get focusElement() {
    return this.querySelector('input[type="search"]');
  }
}
customElements.define('search-drawer', SearchDrawer);

// --- MonBlari: make header search icon go to full search page (internal use) ---
(function () {
  const TARGET_URL = "/search?options[prefix]=last&q="; // 你要跳的页面

  function isHeaderSearchIcon(el) {
    if (!el) return false;
    // 兼容：按钮 / 链接 / 图标容器
    return !!el.closest(
      'a[href*="search"], button[aria-label*="Search"], a[aria-label*="Search"], button[class*="search"], a[class*="search"], [data-search], [data-drawer*="search"]'
    );
  }

  document.addEventListener(
    "click",
    function (e) {
      const t = e.target;
      if (!isHeaderSearchIcon(t)) return;

      // 只拦截 header 区域里的搜索图标（避免干扰其它 search link）
      const header = t.closest("header, .header, #shopify-section-header, [data-section-type*='header']");
      if (!header) return;

      // 阻止 drawer 打开
      e.preventDefault();
      e.stopPropagation();

      // 直接跳转到 search 页面
      window.location.href = TARGET_URL;
    },
    true
  );
})();

// --- MonBlari: suppress predictive dropdown / "no results" for model-like queries ---
(function () {
  function looksLikeModel(q) {
    if (!q) return false;
    const s = q.trim();
    if (s.length < 2) return false;

    // 纯数字（如 8507）或 字母+数字（如 XA-060, XY-1007）
    const isDigits = /^\d+$/.test(s);
    const hasLetterAndDigit = /[a-zA-Z]/.test(s) && /\d/.test(s);
    return isDigits || hasLetterAndDigit;
  }

  function hidePredictiveNoResults(root) {
    if (!root) root = document;
    const nodes = root.querySelectorAll(
      ".predictive-search__result--empty, .predictive-search__results--empty, .predictive-search__result-empty, [data-predictive-search-no-results]"
    );
    nodes.forEach((n) => (n.style.display = "none"));
  }

  function closePredictive(root) {
    if (!root) root = document;

    // 1) 关掉自定义元素/容器的 open 状态
    const ps = root.querySelector("predictive-search") || root.querySelector(".predictive-search");
    if (ps) {
      ps.classList.remove("is-open");
      ps.removeAttribute("open");
    }

    // 2) 强制隐藏结果容器（多主题兜底）
    const results =
      root.querySelector("#predictive-search-results") ||
      root.querySelector("[data-predictive-search-results]") ||
      root.querySelector(".predictive-search__results");
    if (results) results.style.display = "none";

    // 3) 顺便把 “no results” 也干掉
    hidePredictiveNoResults(root);
  }

  document.addEventListener(
    "input",
    function (e) {
      const el = e.target;
      if (!el || !el.matches('input[type="search"]')) return;

      const q = (el.value || "").trim();
      if (!looksLikeModel(q)) return;

      // 只处理 header 里的 search input（避免干扰别的搜索框）
      const header = el.closest("header, .header, #shopify-section-header, [data-section-type*='header']");
      if (!header) return;

      closePredictive(document);
    },
    true
  );

  // 有些主题是异步渲染 no-results，这里再兜底一下
  document.addEventListener(
    "predictive-search:render",
    function () {
      hidePredictiveNoResults(document);
    },
    true
  );
})();
